/**
 * 전역 메일 작성 모달 상태 (v2.108.0)
 *
 * pdf-preview.ts 와 같은 외부 store 패턴 — 어느 화면·plain 함수에서든
 * openMailCompose() 만 부르면 App 에 한 번 마운트된 MailComposeModal 이 뜬다.
 * 문서 화면마다 모달을 각자 두면 통로가 늘 때마다 구멍이 생긴다.
 */

import type { MailAttachmentMeta, MailDocRef } from './mail-api';
import type { RichDoc } from './rich-doc';

/**
 * 답장·전달 (v2.205.0 · 메일 메뉴) — 창은 원문을 **보여 주기만** 하고, 인용은 서버가 원문을 다시 받아 짓는다.
 */
export interface MailComposeQuote {
  mailId: number;
  mode: 'reply' | 'replyAll' | 'forward';
  /** 창에 보여 줄 원문 제목·보낸 사람 (보내는 내용에는 안 쓰인다 — 서버가 원문에서 다시 읽는다) */
  originalSubject: string;
  originalFrom: string;
  /** 전달일 때 함께 보낼 수 있는 원문 첨부(번호 0 이상) — 처음엔 전부 골라 둔다 */
  attachments?: MailAttachmentMeta[];
}

export interface MailComposeInit {
  /** 미리 채울 받는사람 (v2.140.0 · 딜등록 만료 안내 — 제안사 칸에서 찾은 주소). 창에서 고칠 수 있다 */
  to?: string[];
  /** 발송이 끝난 뒤 부를 것 (v2.140.0) — 딜등록이 「보냈다」 날짜를 적는 데 쓴다. 실패 시엔 안 부른다 */
  onSent?: (result: { to: string[]; cc: string[] }) => void;
  /** 서버가 만들/꺼낼 문서 첨부 (docType + id 좌표만) */
  docRefs?: MailDocRef[];
  /** 미리 채워 둘 로컬 파일 (화면이 이미 blob 으로 받아 둔 산출물 등) */
  files?: File[];
  subject?: string;
  body?: string;
  /** 미리 채울 참조 (v2.190.0 · 딜등록 안내 — 인포트렌드 공동 담당자). 창에서 고칠 수 있다.
   *  ⚠sales@smsys.co.kr 은 서버가 항상 넣으므로 여기 적지 않는다. */
  cc?: string[];
  /** 서식 있는 본문 (v2.190.0) — **표가 필요할 때** 쓴다. 주면 body 대신 이쪽이 본문이 된다 */
  bodyDoc?: RichDoc;
  /** [+ 포털 보관 문서] 선택기의 기본 영업건 (알고 있으면) */
  salesId?: string;
  /** 답장·전달이면 원문 정보 (v2.205.0) */
  quote?: MailComposeQuote;
}

export interface MailComposeState {
  open: boolean;
  init: MailComposeInit;
  /** 모달 강제 리셋용 — 열 때마다 증가 */
  seq: number;
}

let state: MailComposeState = { open: false, init: {}, seq: 0 };
const listeners = new Set<() => void>();

function emit(): void {
  for (const fn of listeners) fn();
}

export function subscribeMailCompose(fn: () => void): () => void {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
}

export function getMailComposeState(): MailComposeState {
  return state;
}

export function openMailCompose(init: MailComposeInit = {}): void {
  state = { open: true, init, seq: state.seq + 1 };
  emit();
}

export function closeMailCompose(): void {
  if (!state.open) return;
  state = { ...state, open: false };
  emit();
}
